"use client";
import React, { useCallback, useState, useEffect, useRef } from "react";
import styles from "./index.module.scss";
import { formatTimestamp } from "@/app/utils/date";
import Image from "next/image";
import {
  RobotMessage,
  RobotMessageType,
  ScrollToBottom,
} from "@/app/store/types";
import { getPublicImagePath } from "@/app/utils/image";
import DangerouslySetInnerHTML from "@/app/ui/dangerouslySetInnerHTML";
import GuideMenu from "../components/guideMenu";
import AiAnswerQuestion from "../components/aiAnswerQuestion";
import AiAnswerHistory from "../components/aiAnswerHistory";
import { ScrollAction } from "@/app/hooks/useReadScrollToBottom";
import TechAIFooter from "../components/techAIFooter";
import Feedback from "../components/aiFeedback";
import { ReportParentContentEnum } from "@/app/types/typeConfig/report";
import { IMConfigTypeEnum } from "@/app/types/typeConfig/chatQuestion";
import { AIQuestionList } from "@/app/hooks/useClientInit";
import FeedFooter from "../components/feedFooter";
import SelectHotQuestion, {
  mobileRobotAIKeywordItem,
} from "../components/mobile/selectHotAIQuestion";
import { useMessageStore } from "@/app/store";

interface IProps {
  msg: RobotMessage & {
    handleScrollToBottom?: ScrollToBottom;
  };
}

// 定义动态组件的类型
type DynamicComponent =
  | React.ComponentType<Record<string, unknown>>
  | React.ElementType;

// 机器人消息组件 - 支持富文本渲染的聊天气泡
export default function RobotMessageItem({ msg }: IProps) {
  const {
    id,
    content,
    timestamp,
    senderName,
    messageType,
    showFeedFooter = false,
    error,
    robotData,
    streaming,
    handleScrollToBottom,
  } = msg;

  /**
   * 状态说明：
   * - streaming: 外部传入，表示数据流是否正在接收
   * - typingEnd: 打字机是否完成打印（包括自然完成和用户停止后的完成）
   */
  const [typingEnd, setTypingEnd] = useState(
    messageType !== RobotMessageType.ROBOT_AI &&
      messageType !== RobotMessageType.ROBOT_SSE_REPLY,
  );
  const [userStopped, setUserStopped] = useState(false);

  // 获取 store 方法来同步 typingEnd 状态
  const { setAiTypingEnd, aiTypingEnd } = useMessageStore();

  // 使用 ref 来追踪 feedFooter 的 DOM 元素
  const feedFooterRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // 处理AI打字时的滚动
  const handleAIContentUpdate = useCallback(() => {
    if (handleScrollToBottom) {
      handleScrollToBottom(ScrollAction.AUTO);
    }
  }, [handleScrollToBottom]);

  // 主动停止打印机打字
  // const handleStop = useCallback(() => {
  //   setUserStopped(true);
  //   setTypingEnd(true);
  //   if (typeof robotData?.stopStreaming === "function") {
  //     robotData.stopStreaming();
  //   }
  // }, [robotData]);

  // AI打字自己结束
  const handleAITypingEnd = useCallback(() => {
    setTypingEnd(true);
    setAiTypingEnd(true); // 同步到全局 store
  }, [setAiTypingEnd]);

  // 处理重新生成
  const handleRedo = useCallback(() => {
    setTypingEnd(false);
    setAiTypingEnd(false); // 同步到全局 store
    if (typeof robotData?.retry === "function") {
      robotData.retry(id);
    }
  }, [robotData, id, setAiTypingEnd]);

  // 当 feedFooter 或 ACTION_CARD 渲染完成后，触发滚动
  // 使用 useEffect 监听 feedFooterRef 的变化
  useEffect(() => {
    if (
      (showFeedFooter || messageType === RobotMessageType.ROBOT_ACTION_CARD) &&
      handleScrollToBottom
    ) {
      // 使用 MutationObserver 监听 DOM 变化，确保内容完全加载
      const observer = new MutationObserver(() => {
        handleScrollToBottom(ScrollAction.AUTO);
      });

      // 监听内容区域的变化
      if (contentRef.current) {
        observer.observe(contentRef.current, {
          childList: true,
          subtree: true,
          attributes: true,
        });
      }

      // 立即触发一次滚动
      handleScrollToBottom(ScrollAction.AUTO);

      // 多次延迟触发，确保所有异步内容加载完成
      // 第一次：100ms 后触发（处理快速渲染的情况）
      const timer1 = setTimeout(() => {
        handleScrollToBottom(ScrollAction.AUTO);
      }, 100);

      return () => {
        observer.disconnect();
        clearTimeout(timer1);
      };
    }
  }, [showFeedFooter, messageType, handleScrollToBottom]);

  // 根据不同的机器人消息类型渲染不同的内容
  const renderContent = () => {
    switch (messageType) {
      case RobotMessageType.ROBOT_WELCOME:
        return (
          <div className={styles.welcomeContent}>
            <DangerouslySetInnerHTML msgContent={content} />
          </div>
        );

      case RobotMessageType.ROBOT_RECOMMEND_QUESTION:
        return <GuideMenu data={robotData?.questions as AIQuestionList} />;

      case RobotMessageType.ROBOT_FAQ:
        return (
          <div className={styles.welcomeContent}>
            <DangerouslySetInnerHTML msgContent={robotData?.reply as string} />
          </div>
        );

      case RobotMessageType.ROBOT_SSE_REPLY:
        return (
          <AiAnswerQuestion
            content={content}
            streaming={streaming}
            error={error}
            staticDisplay={streaming !== true}
            onContentUpdate={handleAIContentUpdate}
            onTypingEnd={handleAITypingEnd}
          />
        );
      case RobotMessageType.ROBOT_SSE_HISTORY:
        return <AiAnswerHistory content={content} />;
      case RobotMessageType.ROBOT_FEEDBACK:
        return (
          <Feedback
            templateProps={{
              likeOrDislike: robotData?.feedback as "like" | "dislike",
              requestId: robotData?.requestId as string,
            }}
            parentContentInfo={
              robotData?.parentContentInfo as {
                parentContentId: string;
                parentContent: string;
                parentContentType: ReportParentContentEnum;
              }
            }
            configType={robotData?.configType as IMConfigTypeEnum}
          />
        );
      case RobotMessageType.ROBOT_ACTION_CARD:
        const Component = robotData?.template as DynamicComponent;
        return (
          Component && (
            <Component
              key={robotData?.uniqueKey}
              {...robotData}
              handleScrollToBottom={handleScrollToBottom}
            ></Component>
          )
        );
      case RobotMessageType.ROBOT_HANDY_SELECT_HOT_QUESTION:
        return (
          <SelectHotQuestion
            id="mobile_robot_hot_question"
            title={robotData?.title as string}
            data={robotData?.questionList as mobileRobotAIKeywordItem[]}
          />
        );
      default:
        return <DangerouslySetInnerHTML msgContent={content} />;
    }
  };

  // 获取机器人名称
  const getRobotName = () => {
    return senderName || "智能助手";
  };

  return (
    // Dialog 消息不渲染外层容器
    <>
      {robotData && robotData.type === "Dialog" ? (
        renderContent()
      ) : (
        <div className={`${styles.robotMessage} ${styles.service}`}>
          {/* 消息信息 */}
          <div className={styles.messageInfo}>
            {/* <div className={styles.messageSender}>{getRobotName()}</div> */}
            <div className={styles.messageTime}>
              {formatTimestamp(timestamp)}
            </div>
          </div>

          {/* 消息主体 */}
          <div className={styles.messageInner}>
            {/* 头像 */}
            <div className={styles.messageHeader}>
              <div className={styles.messageAvatar}>
                <Image
                  src={getPublicImagePath("aiAvatar.svg")}
                  alt="robot avatar"
                  width={36}
                  height={36}
                />
              </div>
            </div>

            {/* 消息内容容器 */}
            <div className={styles.messageContentContainer} ref={contentRef}>
              <div
                className={styles.messageContent}
                data-message-type={messageType}
              >
                {renderContent()}
              </div>
              {/* AI 实时回复与历史 SSE 形态回复均展示底部操作栏 */}
              {(messageType === RobotMessageType.ROBOT_SSE_REPLY ||
                messageType === RobotMessageType.ROBOT_SSE_HISTORY) && (
                <div className={styles.techAIFooter}>
                  <TechAIFooter
                    data={robotData}
                    requestId={robotData?.requestId as string}
                    error={error}
                    content={content}
                    aiTypingEnd={aiTypingEnd}
                    // onStop={handleStop}
                    onRedo={handleRedo}
                  />
                </div>
              )}
              {/* 普通信息的底部栏 */}
              {showFeedFooter ? (
                <div className={styles.techAIFooter} ref={feedFooterRef}>
                  <FeedFooter
                    data={robotData}
                    requestId={robotData?.requestId as string}
                    content={
                      content ||
                      (robotData?.reply as string) ||
                      (robotData?.message as string) ||
                      ((robotData?.data as { reply?: string })?.reply as string)
                    }
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
